import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Permission, decodePermissions, has } from '@callzone/shared';
import type { Message } from '@callzone/shared';
import { api } from '../lib/api';
import { gateway } from '../lib/gateway';
import {
  displayName,
  memberAccent,
  memberColor,
  useActiveChannel,
  useActiveGuild,
  useAliases,
  useStore,
} from '../store';
import { Avatar } from './Avatar';
import { PencilIcon, SendIcon, TrashIcon } from './icons';

/** Agrupa mensagens seguidas da mesma pessoa dentro de 5 minutos. */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

/**
 * Constantes, e nao literais dentro do seletor: o zustand compara o resultado
 * por referencia, entao devolver um `[]` novo a cada render faz o React
 * re-renderizar para sempre ("Maximum update depth exceeded").
 */
const SEM_MENSAGENS: Message[] = [];
const SEM_DIGITANDO: Record<string, number> = {};

function formatTime(timestamp: number) {
  return new Date(timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatDay(timestamp: number) {
  return new Date(timestamp).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function ChatView() {
  const channel = useActiveChannel();
  const guild = useActiveGuild();
  const me = useStore((s) => s.me);
  const messages = useStore((s) => (channel ? (s.messages[channel.id] ?? SEM_MENSAGENS) : SEM_MENSAGENS));
  const loaded = useStore((s) => (channel ? !!s.loadedChannels[channel.id] : false));
  const setMessages = useStore((s) => s.setMessages);
  const toast = useStore((s) => s.toast);
  const typing = useStore((s) => (channel ? (s.typing[channel.id] ?? SEM_DIGITANDO) : SEM_DIGITANDO));
  // Assina os apelidos para o autor das mensagens acompanhar uma troca.
  useAliases();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const permissions = decodePermissions(guild?.permissions);
  const canSend = has(permissions, Permission.SEND_MESSAGES);
  const canManage = has(permissions, Permission.MANAGE_MESSAGES);

  useEffect(() => {
    if (!channel || loaded) return;
    let cancelled = false;
    void api
      .messages(channel.id)
      .then((result) => {
        if (!cancelled) setMessages(channel.id, result.messages);
      })
      .catch((error: Error) => {
        if (!cancelled) toast(error.message, 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [channel?.id, loaded, setMessages, toast, channel]);

  // Rola para o fim quando chega mensagem nova, mas so se ja estava no fim.
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 220;
    if (nearBottom) element.scrollTop = element.scrollHeight;
  }, [messages.length, channel?.id]);

  if (!channel || !guild) return null;

  const typingNames = Object.entries(typing)
    .filter(([, at]) => Date.now() - at < 6000)
    .map(([userId]) => displayName(guild, userId));

  async function saveEdit(messageId: string) {
    const content = editText.trim();
    setEditing(null);
    if (!content) return;
    try {
      await api.editMessage(messageId, content);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Falha ao editar.', 'error');
    }
  }

  return (
    <div className="chat">
      <div className="messages" ref={scrollRef}>
        <div className="channel-intro">
          <h2>#{channel.name}</h2>
          <p>
            {channel.topic ?? `Este e o comeco do canal #${channel.name}.`}
          </p>
        </div>

        {messages.map((message, index) => {
          const previous = messages[index - 1];
          const sameDay =
            previous && new Date(previous.createdAt).toDateString() === new Date(message.createdAt).toDateString();
          const grouped =
            !!previous &&
            sameDay &&
            previous.authorId === message.authorId &&
            message.createdAt - previous.createdAt < GROUP_WINDOW_MS;

          return (
            <div key={message.id}>
              {!sameDay && (
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: 11,
                    color: 'var(--text-dim)',
                    margin: '14px 0 8px',
                  }}
                >
                  {formatDay(message.createdAt)}
                </div>
              )}
              <MessageRow
                message={message}
                grouped={!!grouped}
                editing={editing === message.id}
                editText={editText}
                onEditTextChange={setEditText}
                onStartEdit={() => {
                  setEditing(message.id);
                  setEditText(message.content);
                }}
                onCancelEdit={() => setEditing(null)}
                onSaveEdit={() => void saveEdit(message.id)}
                canDelete={canManage || message.authorId === me?.id}
                canEdit={message.authorId === me?.id}
                onDelete={async () => {
                  if (!window.confirm('Apagar esta mensagem?')) return;
                  await api.deleteMessage(message.id).catch((error: Error) => toast(error.message, 'error'));
                }}
              />
            </div>
          );
        })}

        {loaded && messages.length === 0 && (
          <div style={{ color: 'var(--text-dim)', padding: '8px' }}>
            Nenhuma mensagem por aqui ainda.
          </div>
        )}
      </div>

      <div className="composer">
        {canSend ? (
          <Composer channelId={channel.id} channelName={channel.name} />
        ) : (
          <div className="locked-composer">
            Voce nao tem permissao para enviar mensagens neste canal.
          </div>
        )}
        <div className="typing-line">
          {typingNames.length > 0 &&
            `${typingNames.slice(0, 3).join(', ')} ${typingNames.length === 1 ? 'esta' : 'estao'} digitando...`}
        </div>
      </div>
    </div>
  );
}

interface MessageRowProps {
  message: Message;
  grouped: boolean;
  editing: boolean;
  editText: string;
  onEditTextChange: (value: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
  canDelete: boolean;
}

function MessageRow({
  message,
  grouped,
  editing,
  editText,
  onEditTextChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  canEdit,
  canDelete,
}: MessageRowProps) {
  const guild = useActiveGuild();
  const name = displayName(guild, message.authorId);
  const color = memberColor(guild, message.authorId);

  return (
    <div className={`message ${grouped ? 'grouped' : ''}`}>
      <div className="gutter">
        {grouped ? (
          <span className="timestamp">{formatTime(message.createdAt)}</span>
        ) : (
          <Avatar name={name} color={memberAccent(guild, message.authorId)} size={38} />
        )}
      </div>
      <div className="body">
        {!grouped && (
          <div className="meta">
            <span className="author" style={color ? { color } : undefined}>
              {name}
            </span>
            <span className="when">{formatTime(message.createdAt)}</span>
          </div>
        )}
        {editing ? (
          <div>
            <textarea
              value={editText}
              autoFocus
              rows={2}
              onChange={(e) => onEditTextChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSaveEdit();
                }
                if (e.key === 'Escape') onCancelEdit();
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
              Enter salva · Esc cancela
            </div>
          </div>
        ) : (
          <div className="content">
            {message.content}
            {message.editedAt && <span className="edited">(editado)</span>}
          </div>
        )}
      </div>

      {!editing && (canEdit || canDelete) && (
        <div className="message-actions">
          {canEdit && (
            <button title="Editar" onClick={onStartEdit}>
              <PencilIcon />
            </button>
          )}
          {canDelete && (
            <button title="Apagar" onClick={onDelete}>
              <TrashIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Composer({ channelId, channelName }: { channelId: string; channelName: string }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const toast = useStore((s) => s.toast);
  const lastTypingRef = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setText('');
    textareaRef.current?.focus();
  }, [channelId]);

  // Altura do campo acompanha o conteudo, ate o limite do CSS.
  useLayoutEffect(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [text]);

  async function send() {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    setText('');
    try {
      await api.sendMessage(channelId, content);
    } catch (error) {
      setText(content);
      toast(error instanceof Error ? error.message : 'Falha ao enviar.', 'error');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="composer-inner">
      <textarea
        ref={textareaRef}
        rows={1}
        value={text}
        placeholder={`Conversar em #${channelName}`}
        onChange={(e) => {
          setText(e.target.value);
          // Avisa "digitando" no maximo a cada 3s.
          if (Date.now() - lastTypingRef.current > 3000) {
            lastTypingRef.current = Date.now();
            gateway.startTyping(channelId);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void send();
          }
        }}
      />
      {/*
       * Enter continua enviando, mas no celular nao ha Enter confortavel e nem
       * todo mundo adivinha que Shift+Enter quebra linha. O botao torna a acao
       * visivel.
       */}
      <button
        className="composer-enviar"
        title="Enviar"
        aria-label="Enviar mensagem"
        disabled={sending || !text.trim()}
        onClick={() => void send()}
      >
        <SendIcon />
      </button>
    </div>
  );
}
